import { computed, ref, unref, watch } from 'vue'
import { useHttp } from '@/shared/lib'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'

export type RelatedObjectCard = {
  fieldId: string
  fieldName: string
  objectId: number
  name: string
  typeName: string
  status: string | null
}

export function usePassportRuntimeRelated(
  fieldMetadata: ObjectInventoryField[] | { value: ObjectInventoryField[] },
  fieldValues: Record<string, string> | { value: Record<string, string> },
  statusDate: string | null | { value: string | null },
) {
  const http = useHttp()
  const relatedObjects = ref<RelatedObjectCard[]>([])
  const isLoading = ref(false)
  const hasError = ref(false)

  const objectLinkFields = computed(() => {
    const meta = Array.isArray(fieldMetadata) ? fieldMetadata : (fieldMetadata as any).value ?? []
    return meta.filter((f) => String(f.type) === 'object_link')
  })

  watch(
    () => [objectLinkFields.value, { ...(unref(fieldValues) ?? {}) }, unref(statusDate)] as const,
    async () => {
      const fields = objectLinkFields.value
      const values = unref(fieldValues) ?? {}
      const date = unref(statusDate)
      const linked = fields
        .map((f) => {
          const val = values[String(f.id)]
          const id = Number(val)
          return { field: f, id: Number.isFinite(id) && id > 0 ? id : 0 }
        })
        .filter((x) => x.id > 0)

      if (linked.length === 0) {
        relatedObjects.value = []
        hasError.value = false
        return
      }

      isLoading.value = true
      hasError.value = false
      try {
        const params = new URLSearchParams()
        params.set('object_ids', linked.map((x) => x.id).join(','))
        if (date) {
          params.set('status_date', date)
        }
        const res = await http.get(`/inventory/objects/passport?${params.toString()}`)
        const data = (res.data?.data ?? []) as Array<{
          id?: number
          name?: string
          invent_object_type?: { object_name?: string }
          object_type?: { object_name?: string }
          accept_status?: string | null
        }>
        const cards: RelatedObjectCard[] = []
        for (const item of data) {
          const match = linked.find((x) => x.id === Number(item.id))
          if (!match) continue
          cards.push({
            fieldId: String(match.field.id),
            fieldName: match.field.name?.trim() || 'Ссылка на объект',
            objectId: match.id,
            name: item.name?.trim() || '—',
            typeName:
              item.invent_object_type?.object_name?.trim() ||
              item.object_type?.object_name?.trim() ||
              '—',
            status: item.accept_status ?? null,
          })
        }
        relatedObjects.value = cards
      } catch {
        hasError.value = true
        relatedObjects.value = []
      } finally {
        isLoading.value = false
      }
    },
    { immediate: true, deep: true },
  )

  return {
    relatedObjects: computed(() => relatedObjects.value),
    isLoading: computed(() => isLoading.value),
    hasError: computed(() => hasError.value),
    objectLinkFields: computed(() => objectLinkFields.value),
  }
}
